import { memo, useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { PickersDayProps, PickersDay } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'infras/datetime';
import TextField from '@mui/material/TextField';
import {
  black,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import { useRouter } from 'next/router';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

interface Props {
  minDate?: Date | null;
  name?: string;
  placeholder?: string;
  maxDate?: Date;
  isHideToday?: boolean;
  disablePast?: boolean;
  value?: Date | DateTime | null;
  onSelect: any;
  buttonPosition?: 'start' | 'end';
  inputVariant?: 'outlined' | 'standard' | 'filled';
}

class Utils extends AdapterLuxon {
  constructor({ locale }: { locale?: string }) {
    super({ locale });
    if (locale) {
      if (locale.split('-')[0] === 'ja') {
        this.formats.year = 'yyyy年';
        this.formats.month = 'M月';
        this.formats.monthAndYear = 'yyyy年M月';
      }
    }
  }
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const onClick = () => {
    setIsOpen(!isOpen);
  };
  const onClose = () => {
    setIsOpen(false);
  };
  const onChange = useCallback(
    (date: any) => {
      props.onSelect(date);
    },
    [props.onSelect],
  );

  const renderDay = (
    date: any,
    selectedDates: Array<any | null>,
    pickersDayProps: PickersDayProps<any>,
  ) => {
    if (props.isHideToday) {
      pickersDayProps.today = false;
    }
    return <StyledPickersDay {...pickersDayProps} />;
  };

  const route = useRouter();
  const now = new Date();

  return (
    <Container>
      <LocalizationProvider
        dateAdapter={Utils}
        localeText={{
          cancelButtonLabel: t('Cancel'),
          okButtonLabel: t('Ok'),
        }}
        adapterLocale={route.locale}
      >
        <DatePicker
          open={isOpen}
          onClose={onClose}
          showToolbar={false}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: 'rgb(0 0 0 / 15%) 0px 8px 20px 0px',
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, onClick }}
              name={props.name}
              variant={props.inputVariant}
              placeholder={props.placeholder}
              value
            />
          )}
          renderDay={renderDay}
          InputAdornmentProps={{
            position: props.buttonPosition || 'start',
          }}
          minDate={props.minDate === null ? undefined : props.minDate || now}
          maxDate={props.maxDate}
          disablePast={props.disablePast}
          label=""
          disableHighlightToday={props.isHideToday}
          value={props.value}
          onChange={onChange}
          reduceAnimations
          views={['day']}
        />
      </LocalizationProvider>
    </Container>
  );
});

const Container = styled.div`
  & .MuiInputBase-root.Mui-disabled {
    color: ${black};
    cursor: pointer;
  }
  & .MuiInputBase-input.Mui-disabled {
    -webkit-text-fill-color: ${black};
    opacity: 1;
  }
  & .MuiIconButton-root.Mui-disabled {
    color: ${black};
    background-color: initial;
  }
`;

const StyledPickersDay = styled(PickersDay)`
  /* .MuiPickersDay-root { */
  color: ${black};
  background-color: ${white};
  :hover {
    color: ${white};
    background-color: ${primary};
  }
  &.Mui-selected {
    color: ${white};
    background-color: ${primary};
  }
  &.Mui-disabled {
    color: ${gray04};
    background-color: ${white};
  }
  /* } */
`;
export default Component;
